import utils from "./utils.js";

// Desktop zoom: lens, window, and inner zoom types.

const zoomMethods = {
    _getWindowSize() {
        const mode = this.opts.zoomWindowSizeMode || "custom";

        // auto/match both need image or layout data that isn't available before
        // the image finishes loading. Fall back to the user's configured custom
        // dimensions so the window appears at a predictable size immediately.
        if (
            mode !== "custom" &&
            (!this.mainImg.complete || !this.mainImg.naturalWidth)
        ) {
            return {
                w: this.opts.zoomWindowWidth,
                h: this.opts.zoomWindowHeight,
            };
        }

        if (mode === "match") {
            return {
                w: this.mainWrap.offsetWidth,
                h: this.mainWrap.offsetHeight,
            };
        }
        if (mode === "auto") {
            const wR = this.mainWrap.getBoundingClientRect();
            const img = this.mainImg;
            const natW = img.naturalWidth || wR.width;
            const natH = img.naturalHeight || wR.height;
            const aspect = natW / natH || 1;
            const pos = this.opts.zoomWindowPosition;
            const ox = this.opts.zoomWindowOffsetX;

            if (pos === "right" || pos === "left") {
                const availW =
                    pos === "right"
                        ? Math.max(
                              window.innerWidth - wR.right - ox - 24,
                              100,
                          )
                        : Math.max(wR.left - ox - 24, 100);
                const h = Math.round(
                    Math.min(wR.height, window.innerHeight * 0.9),
                );
                const w = Math.min(Math.round(h * aspect), availW);
                return { w: Math.max(w, 100), h: Math.max(h, 100) };
            } else {
                const w = Math.round(wR.width);
                const h = Math.min(
                    Math.round(w / aspect),
                    Math.round(window.innerHeight * 0.5),
                );
                return { w: Math.max(w, 100), h: Math.max(h, 100) };
            }
        }
        return {
            w: this.opts.zoomWindowWidth,
            h: this.opts.zoomWindowHeight,
        };
    },

    _zoomEnter(e) {
        // On mobile, all zoom types fall back to touch-based inner zoom.
        // Bail here so simulated mouse events (fired by touch browsers after
        // a tap) cannot activate the zoom window or lens, which would set
        // unexpected inline styles on wcpg-zoom-window and break the layout.
        if (this._isMobile) return;
        if (this.mainWrap.classList.contains("wcpg-no-zoom")) return;
        const item = this.items[this.index];
        if (!item || item.type === "video") return;

        this.isZooming = true;
        this.mainWrap.classList.add("wcpg-zooming");
        this.mainDisplay.style.cursor = this.opts.zoomCursor;

        const src = item.zoomSrc || item.src;

        if (this.opts.zoomType === "lens") {
            // Use a clean url() — escape any special chars safely
            this.lens.style.backgroundImage =
                "url(" + JSON.stringify(src) + ")";
            this.lens.classList.add("wcpg-lens-active");
        }

        if (this.opts.zoomType === "window") {
            // Recalculate size for auto/match modes on every zoom start
            const ws = this._getWindowSize();
            this._cachedWinSize = ws;
            this.zoomWin.style.width = ws.w + "px";
            this.zoomWin.style.height = ws.h + "px";

            this.zoomWin.style.backgroundImage =
                "url(" + JSON.stringify(src) + ")";
            this.zoomWin.classList.add("wcpg-zoom-win-active");
            this._positionZoomWindow();
            if (this.opts.zoomWindowCursor) {
                this.winCursor.classList.add("wcpg-win-cursor-active");
            }
        }

        if (this.opts.zoomType === "inner") {
            // Swap to the full-resolution source for sharp inner zoom.
            // _preloadAll() already fetched zoomSrc on init so this is a
            // cache hit — no visible loading delay.
            if (src && this.mainImg.src !== src) {
                this.mainImg.src = src;
            }
        }

        this._zoomMove(e);
        // Snap the easing start position to the current target so the lens
        // appears immediately under the cursor — prevents sliding in from a
        // stale/initial position (e.g. after switching anchor stacked items).
        this._zs.lensX = this._zs.targetX;
        this._zs.lensY = this._zs.targetY;
        if (this.opts.zoomEasing && this.opts.zoomType !== "inner")
            this._startEasingLoop();
        if (this.opts.onZoomStart) this.opts.onZoomStart.call(this);
    },

    _getImageBounds() {
        // Returns the actual rendered image rectangle { left, top, width, height }
        // relative to mainWrap, accounting for object-fit: contain letterboxing.
        const img = this.mainImg;
        const cW = this.mainDisplay.offsetWidth;
        const cH = this.mainDisplay.offsetHeight;
        if (
            !img.naturalWidth ||
            !img.naturalHeight ||
            this.opts.mainImageFit === "cover"
        )
            return { left: 0, top: 0, width: cW, height: cH };
        const scale = Math.min(
            cW / img.naturalWidth,
            cH / img.naturalHeight,
        );
        const iW = img.naturalWidth * scale;
        const iH = img.naturalHeight * scale;
        return {
            left: (cW - iW) / 2,
            top: (cH - iH) / 2,
            width: iW,
            height: iH,
        };
    },

    _zoomMove(e) {
        if (this._isMobile) return;
        this._lastMouseEvent = e;
        if (!this.isZooming) {
            // The cursor can already be hovering the main image when its
            // mouseenter fires while the image is still loading — _zoomEnter
            // bails out via the wcpg-no-zoom guard in that case, and without
            // this, zoom would stay dead until the mouse actually leaves and
            // re-enters. Retry entry on every subsequent move so zoom starts
            // as soon as the load-gate lifts, without waiting for a fresh
            // mouseenter.
            this._zoomEnter(e);
            return;
        }
        const rect = this.mainWrap.getBoundingClientRect();
        const imgB = this._getImageBounds();
        const hs = this.opts.lensSize / 2;

        // Clamp mouse to actual image area (not the full letterboxed container)
        const mx = utils.clamp(
            e.clientX - rect.left,
            imgB.left,
            imgB.left + imgB.width,
        );
        const my = utils.clamp(
            e.clientY - rect.top,
            imgB.top,
            imgB.top + imgB.height,
        );

        // pct relative to image bounds (0-1 across the actual image)
        const pctX = (mx - imgB.left) / imgB.width;
        const pctY = (my - imgB.top) / imgB.height;

        this._zs.targetX = utils.clamp(
            mx - hs,
            imgB.left,
            imgB.left + imgB.width - this.opts.lensSize,
        );
        this._zs.targetY = utils.clamp(
            my - hs,
            imgB.top,
            imgB.top + imgB.height - this.opts.lensSize,
        );
        this._zs.pctX = pctX;
        this._zs.pctY = pctY;
        this._zs.rect = rect;
        this._zs.imgB = imgB;

        // When easing is off OR for inner type, apply immediately
        if (!this.opts.zoomEasing || this.opts.zoomType === "inner") {
            this._zs.lensX = this._zs.targetX;
            this._zs.lensY = this._zs.targetY;
            this._applyZoom(pctX, pctY, rect);
        }
    },

    _zoomLeave() {
        if (this._isMobile) return;
        if (!this.mainWrap) return;
        this.isZooming = false;
        this.mainWrap.classList.remove("wcpg-zooming");
        this.mainDisplay.style.cursor = "";
        if (this.lens) this.lens.classList.remove("wcpg-lens-active");
        if (this.zoomWin) this.zoomWin.classList.remove("wcpg-zoom-win-active");
        if (this.winCursor) this.winCursor.classList.remove("wcpg-win-cursor-active");
        if (this.opts.zoomType === "inner") {
            this.mainImg.style.transition = "transform 0.25s ease";
            this.mainImg.style.transform = "";
            const snapIndex = this.index;
            const snapSrc = this.items[snapIndex]?.src;
            setTimeout(() => {
                this.mainImg.style.transition = "";
                // Restore the display-size image only if the user hasn't
                // re-entered zoom or navigated to a different image.
                if (
                    !this.isZooming &&
                    this.index === snapIndex &&
                    snapSrc
                ) {
                    this.mainImg.src = snapSrc;
                }
            }, 260);
        }
        if (this._zs.raf) {
            cancelAnimationFrame(this._zs.raf);
            this._zs.raf = null;
        }
        if (this.opts.onZoomEnd) this.opts.onZoomEnd.call(this);
    },

    _startEasingLoop() {
        if (this._zs.raf) cancelAnimationFrame(this._zs.raf);
        const factor = 1 / utils.clamp(this.opts.zoomEasingAmount, 1, 40);
        const ls = this.opts.lensSize;
        const loop = () => {
            if (!this.isZooming) return;
            this._zs.lensX = utils.lerp(
                this._zs.lensX,
                this._zs.targetX,
                factor,
            );
            this._zs.lensY = utils.lerp(
                this._zs.lensY,
                this._zs.targetY,
                factor,
            );
            if (this._zs.rect) {
                const rect = this._zs.rect;
                const imgB = this._zs.imgB || {
                    left: 0,
                    top: 0,
                    width: rect.width,
                    height: rect.height,
                };
                // Derive pct from eased lens position relative to image bounds
                const easedPctX =
                    (this._zs.lensX + ls / 2 - imgB.left) / imgB.width;
                const easedPctY =
                    (this._zs.lensY + ls / 2 - imgB.top) / imgB.height;
                this._applyZoom(easedPctX, easedPctY, rect);
            }
            this._zs.raf = requestAnimationFrame(loop);
        };
        this._zs.raf = requestAnimationFrame(loop);
    },

    /**
     * Core zoom calculation.
     *
     * LENS / WINDOW zoom technique:
     *   The element (lens or zoom window) uses `background-image` set to the hi-res src.
     *
     *   background-size is set so that the zoomed image spans:
     *     bgW = displayWidth  × zoomLevel
     *     bgH = displayHeight × zoomLevel
     *
     *   background-position is set so the point under the cursor (pctX, pctY)
     *   is centred inside the viewport (lens or window):
     *     bgX = -(pctX × bgW) + viewportW/2
     *     bgY = -(pctY × bgH) + viewportH/2
     *
     *   This mirrors what the user sees at zoomLevel magnification of the display image,
     *   not the raw pixel dimensions — so it works for mixed-size and small images.
     *
     * INNER zoom: CSS scale transform with transform-origin at cursor.
     */
    _applyZoom(pctX, pctY, rect) {
        const z = this.opts.zoomLevel;
        const ls = this.opts.lensSize;
        const lx = this._zs.lensX;
        const ly = this._zs.lensY;
        // Use actual image bounds so calculations are correct for letterboxed layouts
        const imgB = this._zs.imgB || {
            left: 0,
            top: 0,
            width: rect.width,
            height: rect.height,
        };

        if (this.opts.zoomType === "lens") {
            // Move the lens element
            this.lens.style.left = lx + "px";
            this.lens.style.top = ly + "px";

            // Zoomed background spans the actual image at z× scale
            const bgW = imgB.width * z;
            const bgH = imgB.height * z;

            // Offset so the cursor point is centred in the lens aperture
            const bgX = -(pctX * bgW - ls / 2);
            const bgY = -(pctY * bgH - ls / 2);

            this.lens.style.backgroundSize = `${bgW}px ${bgH}px`;
            this.lens.style.backgroundPosition = `${bgX}px ${bgY}px`;
        }

        if (this.opts.zoomType === "window") {
            const ws = this._cachedWinSize || this._getWindowSize();
            const ww = ws.w;
            const wh = ws.h;
            const bgW = imgB.width * z;
            const bgH = imgB.height * z;

            // No real CSS border on the element — the visual border is the ::after
            // inset box-shadow. The full ww×wh area is the background area, so no
            // border-width offset is needed in these calculations.
            const bgX = -(pctX * bgW - ww / 2);
            const bgY = -(pctY * bgH - wh / 2);

            this.zoomWin.style.backgroundSize = `${bgW}px ${bgH}px`;
            this.zoomWin.style.backgroundPosition = `${utils.clamp(
                bgX,
                -(bgW - ww),
                0,
            )}px ${utils.clamp(bgY, -(bgH - wh), 0)}px`;

            if (this.opts.zoomWindowCursor) {
                const cursorMode =
                    this.opts.zoomWindowCursorSizeMode || "auto";
                const cw =
                    cursorMode === "custom"
                        ? this.opts.zoomWindowCursorWidth || ww / z
                        : ww / z;
                const ch =
                    cursorMode === "custom"
                        ? this.opts.zoomWindowCursorHeight || wh / z
                        : wh / z;
                // Position cursor relative to actual image bounds
                const mouseX = imgB.left + pctX * imgB.width;
                const mouseY = imgB.top + pctY * imgB.height;
                const cx = utils.clamp(
                    mouseX - cw / 2,
                    imgB.left,
                    imgB.left + imgB.width - cw,
                );
                const cy = utils.clamp(
                    mouseY - ch / 2,
                    imgB.top,
                    imgB.top + imgB.height - ch,
                );
                this.winCursor.style.width = cw + "px";
                this.winCursor.style.height = ch + "px";
                this.winCursor.style.left = cx + "px";
                this.winCursor.style.top = cy + "px";
            }
        }

        if (this.opts.zoomType === "inner") {
            this.mainImg.style.transformOrigin = `${pctX * 100}% ${
                pctY * 100
            }%`;
            this.mainImg.style.transform = `scale(${z})`;
            this.mainImg.style.transition = "none";
        }
    },

    _positionZoomWindow() {
        // The zoom window is position:fixed on document.body, so coordinates
        // come directly from getBoundingClientRect() (viewport space).
        // No stage-relative arithmetic needed — this also means theme CSS
        // overflow:hidden on the gallery wrapper can never clip the window.
        const pos = this.opts.zoomWindowPosition;
        const wR = this.mainWrap.getBoundingClientRect();
        const ox = this.opts.zoomWindowOffsetX;
        const oy = this.opts.zoomWindowOffsetY;
        const ws = this._cachedWinSize || this._getWindowSize();
        const w = ws.w;
        const h = ws.h;
        let left = 0,
            top = 0;

        if (pos === "right") {
            left = wR.right + ox;
            top = wR.top + oy;
        }
        if (pos === "left") {
            left = wR.left - w - ox;
            top = wR.top + oy;
        }
        if (pos === "top") {
            left = wR.left + ox;
            top = wR.top - h - oy;
        }
        if (pos === "bottom") {
            left = wR.left + ox;
            top = wR.bottom + oy;
        }

        // Clamp to viewport so the window stays visible on full-width layouts
        // (e.g. anchor stacked items that span the entire content width).
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        left = utils.clamp(left, 0, vw - w);
        top  = utils.clamp(top,  0, vh - h);

        this.zoomWin.style.left = left + "px";
        this.zoomWin.style.top = top + "px";
    },
};

export default zoomMethods;
